import { verboseDebug } from 'classes/helpers/debug.helper';
import useLatest from 'hooks/useLatestRef';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IVerboseComponent } from 'interfaces/i-components';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableProviderProps, RowWithID } from 'interfaces/tables/main';
import { IPageCoordinates } from 'interfaces/tables/pagination';
import { ISortSpec } from 'interfaces/tables/sorting';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { HasSort } from 'lib_ts/interfaces/common/i-extensions';
import { clamp, clone, isNil } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookiesStore } from 'stores/use-cookies-store';

/**
 * TODO: There are also some issues related to initializing two+ sets of columns/data, even if the tables
 * aren't shown at the same time (E.g. PitchPerformance group/shot tables).
 *
 * Due for a general refactor to at least better handle switching between two tables in the same context.
 */

const CONTEXT_NAME = 'TableContext';

const handleSort = (config: {
  a: any;
  b: any;
  column: ITypedTableColumn<any>;
  sort: ISortSpec;
}): number => {
  try {
    const { a, b, column } = config;
    const { key, dir } = config.sort;

    if (column.sortRowsFn) {
      /** special columns with unusual display values, like formatColumn/formatRow functions */
      return column.sortRowsFn(a, b, dir);
    }

    const dataType = typeof a[key];

    if (dataType === 'string') {
      const aStr = (a[key] ?? '').toLowerCase();
      const bStr = (b[key] ?? '').toLowerCase();

      return dir * -StringHelper.safeSort(aStr, bStr);
    }

    /** best attempt */
    return dir * (a[key] > b[key] ? -1 : 1);
  } catch (e) {
    console.error(e);
    return 1;
  }
};

export interface ITableContext {
  contextTarget?: RowWithID;
  setContextTarget: (v: RowWithID | undefined) => void;

  // updated whenever we need to force the table to re-render (e.g. data changed, data reordered)
  tableKey: number;
  readonly updateTableKey: () => void;

  // table should set this to ensure max page is correctly calculated
  readonly setData: (data: RowWithID[]) => void;
  // table should set this to ensure sorting works correctly
  readonly initColumns: (value: ITypedTableColumn<any>[]) => void;

  // CAUTION: model may be undefined
  readonly initAfterChangeSelected: (
    callback: (model: any | undefined) => void
  ) => void;

  // table can set this so that page size changes will be saved in the cookies, will also set page size from cookies if possible
  readonly initPaginationId: (value: PaginationID) => void;

  sortDef?: ISortSpec;
  readonly initBeforeSortFn: (beforeSortFn: () => void) => void;
  beforeSortFn?: () => void;

  /** undefined key => turn off sort
  undefined dir => next sort mode */
  readonly setSort: (value: Partial<ISortSpec>) => void;

  /** based on data length and page size */
  maxPage: number;

  /** initially undefined (i.e. infinite page size), should be updated if pagination is enabled */
  pageSize: number | undefined;

  /** lazy = true => if _pageSize is not undefined, skip the request */
  readonly setPageSize: (value: number, lazy: boolean) => void;

  /** if table is selectable, highlights selected row, reset on pagination, relative to current page */
  selectedCoord: IPageCoordinates;
  readonly setSelectedCoord: (value: IPageCoordinates) => void;

  selectedData: RowWithID | undefined;

  sortedData: RowWithID[];

  pageData: RowWithID[];

  readonly getLastIndexOnPage: (page: number) => number;

  readonly lookupCoordinates: (id: string) => IPageCoordinates;
}

const DEFAULT: ITableContext = {
  setContextTarget: () => console.error(`${CONTEXT_NAME}: not init`),

  tableKey: Date.now(),
  updateTableKey: () => console.error(`${CONTEXT_NAME}: not init`),

  initColumns: () => console.error(`${CONTEXT_NAME}: not init`),
  initPaginationId: () => console.error(`${CONTEXT_NAME}: not init`),
  initAfterChangeSelected: () => console.error(`${CONTEXT_NAME}: not init`),

  setSort: () => console.error(`${CONTEXT_NAME}: not init`),
  initBeforeSortFn: () => console.error(`${CONTEXT_NAME}: not init`),
  setData: () => console.error(`${CONTEXT_NAME}: not init`),

  maxPage: 0,

  pageSize: undefined,
  setPageSize: () => console.error(`${CONTEXT_NAME}: not init`),

  selectedCoord: {
    page: 0,
    index: -1,
    _id: null,
    callbackKey: null,
  },
  setSelectedCoord: () => console.error(`${CONTEXT_NAME}: not init`),
  selectedData: undefined,

  sortedData: [],

  pageData: [],

  getLastIndexOnPage: () => -1,

  lookupCoordinates: () => ({
    page: 0,
    index: 0,
    _id: null,
    callbackKey: null,
  }),
};

export const TableContext = createContext(DEFAULT);

type IProps = ITableProviderProps & IVerboseComponent & { children: ReactNode };

export const TableProvider = (props: IProps) => {
  const [identifier, setIdentifier] = useState<PaginationID | undefined>();

  const [data, setData] = useState<RowWithID[]>([]);

  const [tableKey, setTableKey] = useState(DEFAULT.tableKey);

  const [sortDef, setSortDef] = useState(DEFAULT.sortDef);

  // force table to redraw whenever data or sorting changes
  useEffect(() => {
    setTableKey(Date.now());
  }, [data, sortDef]);

  const beforeSortCallbackRef = useRef<undefined | (() => void)>();

  const afterSelectedCallbackRef = useRef<
    undefined | ((model: any | undefined) => void)
  >();

  const [pageSize, setPageSize] = useState(DEFAULT.pageSize);

  const maxPage = useMemo(
    () =>
      pageSize === undefined
        ? 0
        : Math.max(0, Math.ceil(data.length / pageSize) - 1),
    [data, pageSize]
  );

  const [columns, setColumns] = useState<ITypedTableColumn<any>[]>([]);

  const sortedData = useMemo(() => {
    const output = clone(data);
    const sortColumn = columns.find((c) => c.key === sortDef?.key);

    if (sortColumn && sortDef && sortDef.dir) {
      output.sort((a, b) =>
        handleSort({
          a: a,
          b: b,
          column: sortColumn,
          sort: sortDef,
        })
      );

      return output;
    }

    /** fallback to sort by _sort column */
    output.sort((a: HasSort, b: HasSort) => (a._sort ?? -1) - (b._sort ?? -1));

    return output;
  }, [data, columns, sortDef, tableKey]);

  // which page of data to show
  const [page, setPage] = useState(0);

  // increasing pageSize which changes maxPage never leaves you on an invalid page
  useEffect(() => {
    setPage((prev) => clamp(prev, 0, maxPage));
  }, [maxPage]);

  const pageData = useMemo(() => {
    if (pageSize === undefined) {
      // everything in one big page
      return sortedData;
    }

    return sortedData.slice(page * pageSize, (page + 1) * pageSize);
  }, [sortedData, page, pageSize]);

  const [selectedCoord, setSelectedCoord] = useState(DEFAULT.selectedCoord);

  const latestCoord = useLatest(selectedCoord);

  // whenever the page data changes, *try* to maintain the selected row
  // e.g. after updating something within a sorted table
  useEffect(() => {
    if (props.skipResetSelected) {
      return;
    }

    if (latestCoord.current.index === -1) {
      return;
    }

    const rowIndex = pageData.findIndex(
      (row) => row._id === latestCoord.current._id
    );

    if (rowIndex === -1) {
      // not found on page, deselect
      setSelectedCoord({
        page: 0,
        index: -1,
        _id: null,
        callbackKey: null,
      });
      return;
    }

    // update to (new) index on the same page
    setSelectedCoord((prev) => ({
      ...prev,
      callbackKey: Date.now(),
      index: rowIndex,
    }));
  }, [pageData, props.skipResetSelected]);

  const selectedData = pageData[selectedCoord.index];

  // trigger callback function whenever coordinate key changes
  useEffect(() => {
    if (!afterSelectedCallbackRef.current) {
      // no callback defined
      return;
    }

    if (!selectedCoord.callbackKey) {
      // key was undefined => skip the callback
      return;
    }

    // todo: confirm if this skip is necessary since the callback should handle undefined case
    // if (!selectedData) {
    // probably don't need this
    //   return;
    // }

    verboseDebug(props, 'coordinate change > callback', selectedCoord);

    afterSelectedCallbackRef.current(selectedData);
  }, [
    selectedCoord.callbackKey,
    // todo: confirm that this can be commented out
    // selectedData,
  ]);

  const getLastIndexOnPage = useCallback(
    (page: number) => {
      if (pageSize === undefined) {
        return sortedData.length - 1;
      }

      const start = page * pageSize;
      const pageData = sortedData.slice(start, start + pageSize);

      const output = pageData.length - 1;
      return output;
    },
    [pageSize, sortedData]
  );

  // callbackKey as null means it won't accidentally trigger after selection logic
  const lookupCoordinates = useCallback(
    (id: string): IPageCoordinates => {
      const sortedIndex = sortedData.findIndex((m) => m._id === id);

      if (pageSize === undefined) {
        // one giant page
        return {
          page: 0,
          index: sortedIndex,
          _id: id,
          callbackKey: null,
        };
      }

      if (sortedIndex === -1) {
        // failed to find => return to first page w/o anything selected
        return {
          page: 0,
          index: -1,
          _id: id,
          callbackKey: null,
        };
      }

      const page = Math.floor(sortedIndex / pageSize);

      return {
        page: page,
        index: sortedIndex - page * pageSize,
        _id: id,
        callbackKey: null,
      };
    },
    [sortedData, pageSize]
  );

  const safeSetSelectedCoord = useCallback(
    (value: IPageCoordinates) => {
      // console.debug('safeSetSelectedCoord', value);

      const safePage = clamp(value.page, 0, maxPage);

      if (isNaN(safePage)) {
        console.warn('cannot change to NaN page');
        return;
      }

      // -1 to allow for selecting nothing on a page
      const safeIndex = clamp(value.index, -1, getLastIndexOnPage(safePage));

      if (isNaN(safeIndex)) {
        console.warn('cannot change to NaN index');
        return;
      }

      setSelectedCoord({
        page: safePage,
        index: safeIndex,
        _id: value._id,
        callbackKey: value.callbackKey,
      });

      setPage(safePage);
    },
    [selectedCoord, maxPage, getLastIndexOnPage]
  );

  // record page size for identified tables in cookies
  useEffect(() => {
    if (isNil(identifier)) {
      return;
    }

    if (isNil(pageSize)) {
      return;
    }

    useCookiesStore.getState().setPageSize(identifier, pageSize);
  }, [identifier, pageSize]);

  const [contextTarget, setContextTarget] = useState(DEFAULT.contextTarget);

  const state: ITableContext = {
    contextTarget: contextTarget,
    setContextTarget: setContextTarget,

    tableKey: tableKey,
    updateTableKey: () => {
      // this will force the table to redraw with the newest order
      setTableKey(Date.now());
    },

    initPaginationId: (value) => {
      setIdentifier(value);

      const defaultPageSize = useCookiesStore.getState().getPageSize(value);
      if (defaultPageSize !== undefined) {
        setPageSize(defaultPageSize);
      }
    },
    setData: setData,
    initColumns: setColumns,
    initAfterChangeSelected: (callback) =>
      (afterSelectedCallbackRef.current = callback),

    sortDef: sortDef,
    setSort: (config) => {
      const { key, dir } = config;

      // deselect (if necessary) before sorting
      if (selectedCoord.index !== -1) {
        setSelectedCoord((prev) => ({
          ...prev,
          index: -1,
        }));
      }

      if (key === undefined) {
        // remove sort, ignore any dir value
        setSortDef(undefined);
        return;
      }

      if (dir !== undefined) {
        // go directly to requested sorting method, probably not triggered
        setSortDef({
          key: key,
          dir: dir,
        });
        return;
      }

      if (key === sortDef?.key) {
        // same key, next direction
        switch (sortDef?.dir) {
          case 1: {
            // next sort
            setSortDef({
              key: key,
              dir: -1,
            });
            return;
          }

          case -1: {
            // remove sort
            setSortDef(undefined);
            return;
          }

          case undefined:
          default: {
            // default sort
            setSortDef({
              key: key,
              dir: 1,
            });
            return;
          }
        }
      }

      // different key, default direction
      setSortDef({
        key: key,
        dir: 1,
      });
    },
    initBeforeSortFn: (callback) => (beforeSortCallbackRef.current = callback),
    beforeSortFn: beforeSortCallbackRef.current,

    sortedData: sortedData,

    maxPage: maxPage,

    pageSize: pageSize,
    setPageSize: (value, lazy) => {
      if (pageSize !== undefined && lazy) {
        return;
      }

      setPageSize(value);
    },

    selectedCoord: selectedCoord,
    setSelectedCoord: safeSetSelectedCoord,
    selectedData: selectedData,

    pageData: pageData,

    getLastIndexOnPage: getLastIndexOnPage,

    lookupCoordinates: lookupCoordinates,
  };

  return (
    <TableContext.Provider value={state}>
      {props.children}
    </TableContext.Provider>
  );
};
